import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RecognitionType } from '@cfl/shared';

import { api, type ApiError } from '@/lib/api';

export interface Recognition {
  id: string;
  type: RecognitionType;
  message: string;
  createdAt: string;
  toEmployee: { id: string; name: string };
  fromEmployee: { id: string; name: string };
}

export interface LeaderboardRow {
  employeeId: string;
  name: string;
  departmentName: string | null;
  count: number;
}

export interface OneOnOne {
  id: string;
  employeeId: string;
  managerId: string;
  scheduledAt: string;
  agenda: string | null;
  notes: string | null;
  /** Null when Calendar was unreachable or the integration is not configured. */
  meetLink: string | null;
  calendarEventId: string | null;
  acknowledgedAt: string | null;
  /** Recorded as never having taken place. The reason is always set with it. */
  notHeldAt: string | null;
  notHeldReason: string | null;
  notHeldBy: { id: string; name: string } | null;
  employee: { id: string; name: string };
  manager: { id: string; name: string };
}

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  linkPath: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  summary: string | null;
  createdAt: string;
  actor: { id: string; name: string; email: string } | null;
  /** Whose record the change was to, e.g. "Abhinav Nair (Q2)". Null when not about one person. */
  subject: string | null;
}

// --- Recognition -------------------------------------------------------------

/** `employeeId` filters by who received it, `fromEmployeeId` by who gave it. */
export function useRecognitionFeed(employeeId?: string, fromEmployeeId?: string) {
  const params = new URLSearchParams();
  if (employeeId) params.set('employeeId', employeeId);
  if (fromEmployeeId) params.set('fromEmployeeId', fromEmployeeId);
  const qs = params.toString();

  return useQuery<Recognition[], ApiError>({
    queryKey: ['recognition', employeeId ?? 'all', fromEmployeeId ?? 'anyone'],
    queryFn: () => api.get<Recognition[]>(`/recognition${qs ? `?${qs}` : ''}`),
  });
}

export function useLeaderboard() {
  return useQuery<LeaderboardRow[], ApiError>({
    queryKey: ['recognition', 'leaderboard'],
    queryFn: () => api.get<LeaderboardRow[]>('/recognition/leaderboard'),
    staleTime: 5 * 60_000,
  });
}

export function useGiveRecognition() {
  const queryClient = useQueryClient();
  return useMutation<
    Recognition,
    ApiError,
    { toEmployeeId: string; type: RecognitionType; message: string }
  >({
    mutationFn: (body) => api.post<Recognition>('/recognition', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['recognition'] }),
  });
}

// --- 1-on-1s -----------------------------------------------------------------

export function useOneOnOnes(enabled = true) {
  return useQuery<OneOnOne[], ApiError>({
    queryKey: ['one-on-ones'],
    queryFn: () => api.get<OneOnOne[]>('/one-on-ones'),
    enabled,
  });
}

function useMeetingMutation<TArgs>(fn: (args: TArgs) => Promise<OneOnOne>) {
  const queryClient = useQueryClient();
  return useMutation<OneOnOne, ApiError, TArgs>({
    mutationFn: fn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['one-on-ones'] }),
  });
}

export const useScheduleOneOnOne = () =>
  useMeetingMutation<{ employeeId: string; scheduledAt: string; agenda?: string | null }>((b) =>
    api.post<OneOnOne>('/one-on-ones', b),
  );

export const useSaveMeetingNotes = () =>
  useMeetingMutation<{ id: string; notes: string }>(({ id, notes }) =>
    api.post<OneOnOne>(`/one-on-ones/${id}/notes`, { notes }),
  );

export const useAcknowledgeMeeting = () =>
  useMeetingMutation<string>((id) => api.post<OneOnOne>(`/one-on-ones/${id}/acknowledge`));

/**
 * Write a 1-on-1 off as never held. The reason is required, and lands in the audit
 * trail beside the actor - the same bar as unassigning a KRA set.
 */
export const useMarkMeetingNotHeld = () =>
  useMeetingMutation<{ id: string; reason: string }>(({ id, reason }) =>
    api.post<OneOnOne>(`/one-on-ones/${id}/not-held`, { reason }),
  );

// --- Notifications -----------------------------------------------------------

export function useNotifications() {
  return useQuery<Notification[], ApiError>({
    queryKey: ['notifications'],
    queryFn: () => api.get<Notification[]>('/notifications'),
    // The bell should feel live without hammering the API.
    refetchInterval: 120_000,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation<{ marked: number }, ApiError, string[] | undefined>({
    mutationFn: (ids) => api.post<{ marked: number }>('/notifications/read', { ids }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// --- Audit -------------------------------------------------------------------

export function useAuditLog(
  filters: { actions?: string; actorId?: string; from?: string; to?: string } = {},
) {
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => Boolean(v)) as [string, string][],
  ).toString();
  return useQuery<AuditRow[], ApiError>({
    queryKey: ['audit', filters],
    queryFn: () => api.get<AuditRow[]>(`/audit${qs ? `?${qs}` : ''}`),
  });
}
